import { quoteIdentityBlurb } from '@/components/quick-study-results/QuickStudyTwinsPanel'
import { QuoteItem } from '@/components/quick-study-results/QuickStudyOpenEndCard'
import { buildMonadicVersionLabelMap, resolveQuoteVersionLabel } from '@/components/quick-study-results/quickStudyPure'
import { Result, TwinResponse } from '@/components/quick-study-results/quickStudyTypes'

import { shortOptionLabel } from './shortOptionLabel'

export interface StudyDocQuote {
  text: string
  name: string
  initials: string
  blurb?: string
  imageUrl?: string
  versionLabel?: string
  /**
   * How the cite should phrase {@link versionLabel}: `'ranked'` reads "ranked X first"
   * (monadic creatives, ranking questions — the historical default), `'chose'` reads
   * "chose X" for a single/multiple choice question, where the respondent picked one
   * option rather than ordering them. Absent → `'ranked'`, today's copy.
   */
  versionLabelKind?: 'ranked' | 'chose'
  segmentLabel?: string
  /** Alias of `name` — kept so older quote maps still type-check. */
  attribution?: string
  /** `personas.token`. Lets a mosaic band ring the tile speaking the active quote. */
  personaId?: string
}

export function isNamedStudyDocQuote(quote: Pick<StudyDocQuote, 'name' | 'attribution'>): boolean {
  const name = (quote.name || quote.attribution || '').trim()
  if (!name) {
    return false
  }
  return name.toLowerCase() !== 'twin'
}

export function initialsFromDisplayName(name: string): string {
  return (
    name
      .split(/\s+/)
      .slice(0, 2)
      .map((word) => word[0]?.toUpperCase() ?? '')
      .join('') || 'T'
  )
}

function nameFromAttributes(attributes: Record<string, unknown> | undefined): string {
  const attrs = attributes ?? {}
  const raw = attrs.display_name ?? attrs.name ?? attrs.persona_name ?? ''
  return String(raw).trim()
}

function imageUrlFromAttributes(attributes: Record<string, unknown> | undefined): string | undefined {
  const attrs = attributes ?? {}
  const raw = attrs.image_url ?? attrs.imageUrl ?? ''
  const url = String(raw).trim()
  return url || undefined
}

function firstSegmentLabel(response: TwinResponse | undefined): string | undefined {
  const labels = response?.segment_labels
  if (!Array.isArray(labels) || labels.length === 0) {
    return undefined
  }
  const label = String(labels[0] ?? '').trim()
  return label || undefined
}

function stringField(row: Record<string, unknown>, keys: Array<string>): string | undefined {
  for (const key of keys) {
    const raw = row[key]
    if (typeof raw === 'string' && raw.trim()) {
      return raw.trim()
    }
  }
  return undefined
}

function responseForPersona(
  responses: Array<TwinResponse>,
  personaId: string,
): TwinResponse | undefined {
  if (!personaId) {
    return undefined
  }
  return responses.find((row) => String(row.persona_id ?? '') === personaId)
}

/**
 * SurveyJS-native single/multiple-choice question types plus the canonical spec aliases
 * the generator emits for the same two shapes. Mirrors `CHOICE_QUESTION_TYPES` in
 * `StudyDocMagazineBand/resolveMagazineBandSource.ts` — declared locally on purpose so
 * quote enrichment never imports band-derivation code.
 */
const CHOICE_QUESTION_TYPES = new Set([
  'radiogroup',
  'dropdown',
  'checkbox',
  'tagbox',
  'imagepicker',
  'single_select',
  'multi_select',
])

function isChoiceResult(result: Result | null): boolean {
  return CHOICE_QUESTION_TYPES.has(String(result?.question?.type ?? '').trim().toLowerCase())
}

/** `question.choices` → a lookup keyed on BOTH the option id/value and the option text,
 *  case-insensitively. A twin's `survey_data` may carry either form depending on how the
 *  survey was authored, and the label minter's map (`buildMonadicVersionLabelMap`) only
 *  keys on the id — which is exactly why a `single_select` run resolved no cite chip. */
function choiceLabelIndex(result: Result | null): Map<string, string> {
  const index = new Map<string, string>()
  const choices = Array.isArray(result?.question?.choices) ? result.question.choices : []
  choices.forEach((choice: unknown) => {
    if (typeof choice === 'string') {
      const label = choice.trim()
      if (label) {
        index.set(label.toLowerCase(), label)
      }
      return
    }
    if (!choice || typeof choice !== 'object') {
      return
    }
    const raw = choice as Record<string, unknown>
    const id = String(raw.value ?? raw.id ?? '').trim()
    const label = String(raw.text ?? raw.label ?? '').trim() || id
    if (!label) {
      return
    }
    if (id) {
      index.set(id.toLowerCase(), label)
    }
    index.set(label.toLowerCase(), label)
  })
  return index
}

/** One scalar answer value, or the first entry of a multi-select array. */
function firstAnswerValue(value: unknown): string {
  const raw = Array.isArray(value) ? value[0] : value
  if (typeof raw === 'string') {
    return raw.trim()
  }
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return String(raw)
  }
  return ''
}

/**
 * The option THIS respondent picked on the featured choice question, as a display label.
 *
 * Why this channel exists (Step 5b visual QA finding 5): `resolveQuoteVersionLabel` only
 * matches an answer against `buildMonadicVersionLabelMap`, which is keyed on option
 * ids/creative ids. A plain `single_select` result whose `question.choices` are absent (or
 * whose `survey_data` stores the option TEXT while the map is keyed on `value`) matches
 * nothing there and yields no chip at all — which is what the live band showed. This reads
 * the respondent's own answer for the result's own question and names it: through
 * `question.choices` when they resolve it, otherwise the answer string itself, which for a
 * text-valued choice already IS the label. Name-agnostic (hard rule 1): the question is
 * identified by `result.question.name`, never by a hard-coded id or option.
 */
function pickedOptionLabel(
  result: Result | null,
  response: TwinResponse | undefined,
): string | undefined {
  const questionName = String(result?.question?.name ?? '').trim()
  if (!questionName || !response) {
    return undefined
  }
  const answer = firstAnswerValue((response.survey_data || {})[questionName])
  if (!answer) {
    return undefined
  }
  return choiceLabelIndex(result).get(answer.toLowerCase()) || answer
}

export function studyDocQuoteFromExhibit(
  row: Record<string, unknown>,
  result: Result | null,
): StudyDocQuote | null {
  const text = typeof row.text === 'string' ? row.text.trim() : ''
  if (!text) {
    return null
  }

  const responses = result?.responses ?? []
  const personaId = typeof row.persona_id === 'string' ? row.persona_id : ''
  const response = responseForPersona(responses, personaId)
  const attributes = (response?.attributes ?? {}) as Record<string, unknown>
  const versionMap = result ? buildMonadicVersionLabelMap(result) : undefined

  const name =
    stringField(row, ['name', 'attribution']) ??
    nameFromAttributes(attributes)
  if (!isNamedStudyDocQuote({ name })) {
    return null
  }

  const blurb =
    stringField(row, ['blurb']) ??
    quoteIdentityBlurb(attributes) ??
    ''
  // Choice questions get a "chose X" chip; everything else keeps today's "ranked X first".
  // The picked-option channel is a FALLBACK after the exhibit's own field and the label
  // minter's map, so a monadic/ranking run resolves exactly as before.
  const isChoice = isChoiceResult(result)
  const resolvedVersionLabel =
    stringField(row, ['version_label', 'versionLabel']) ??
    resolveQuoteVersionLabel({
      versionLabelById: versionMap,
      itemId: response?.item_id,
      surveyData: response?.survey_data,
      quoteText: text,
    }) ??
    (isChoice ? pickedOptionLabel(result, response) : undefined)
  // Survey-authored option labels glue a name to its description ("AI Advantage: A
  // practical guide ..."); the cite chip names the concept only.
  const versionLabel =
    resolvedVersionLabel && isChoice
      ? shortOptionLabel(resolvedVersionLabel) || undefined
      : resolvedVersionLabel
  const segmentLabel =
    stringField(row, ['segment_label', 'segmentLabel']) ??
    firstSegmentLabel(response)
  const imageUrl =
    stringField(row, ['imageUrl']) ??
    imageUrlFromAttributes(attributes)

  return {
    text,
    name,
    attribution: name,
    initials: initialsFromDisplayName(name),
    blurb: blurb || undefined,
    imageUrl,
    versionLabel,
    versionLabelKind: isChoice ? 'chose' : undefined,
    segmentLabel,
    personaId: personaId || undefined,
  }
}

/**
 * Fill a quote's "chose X" chip from ANY choice question in the same document that this
 * speaker answered.
 *
 * Why a cross-result lookup is required (live QA 2026-09-03, thread 9a20d99e): the quote
 * and the pick live in two DIFFERENT results. The verbatims the band rotates come from the
 * follow-up open end ("What is the main reason you would be most interested in the guide
 * you selected?", `question.type: 'comment'`), whose `survey_data` holds only the verbatim
 * ("I went with b because..."). The pick itself is a row on the preceding choice question
 * (`type: 'radiogroup'`), where `survey_data` holds the bare option id ("b") and
 * `question.choices` carry `{ value: 'b', text: 'Trust, Amplified — A guide to...' }`.
 * {@link studyDocQuoteFromExhibit} only ever sees the open end's own Result, so its
 * single-result channel correctly resolves nothing and the chip never rendered.
 *
 * The rule is survey-agnostic (hard rule 1): "name what this persona picked on a choice
 * question in this document". No question ids, no letter handling, no option strings — the
 * letter is simply an option `value` and resolves through `question.choices` like any other
 * id. Results are tried in the caller's order (the band puts its featured result first), the
 * first result carrying an answer for this persona wins, and a quote that already has a
 * label (a python-stamped `version_label`, a monadic creative) is never overwritten.
 */
export function fillPickedOptionChip(
  quote: StudyDocQuote,
  results: Array<Result>,
): StudyDocQuote {
  const personaId = (quote.personaId || '').trim()
  if (!personaId || (quote.versionLabel || '').trim()) {
    return quote
  }
  for (const result of results) {
    if (!isChoiceResult(result)) {
      continue
    }
    const label = pickedOptionLabel(result, responseForPersona(result.responses ?? [], personaId))
    const short = label ? shortOptionLabel(label) : ''
    if (short) {
      return { ...quote, versionLabel: short, versionLabelKind: 'chose' }
    }
  }
  return quote
}

export function normalizeStudyDocQuote(quote: StudyDocQuote): StudyDocQuote | null {
  const name = (quote.name || quote.attribution || '').trim()
  if (!isNamedStudyDocQuote({ name })) {
    return null
  }
  return {
    ...quote,
    name,
    attribution: name,
    initials: quote.initials || initialsFromDisplayName(name),
  }
}

export function studyDocQuoteToItem(quote: StudyDocQuote, key: string): QuoteItem {
  const named = normalizeStudyDocQuote(quote)
  if (!named) {
    return {
      key,
      text: quote.text,
      name: 'Twin',
      blurb: '',
      initials: 'T',
      matched: false,
      segmentLabel: '',
    }
  }
  return {
    key,
    text: named.text,
    name: named.name,
    blurb: named.blurb ?? '',
    initials: named.initials,
    imageUrl: named.imageUrl,
    matched: true,
    segmentLabel: named.segmentLabel ?? '',
    versionLabel: named.versionLabel,
  }
}
